import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { IconAi, IconExpenses, IconInvoices, IconPin, IconProjects } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import { enterDemoMode, shouldOpenSignup } from '../lib/demoMode';
import { LEGAL_LINKS } from '../lib/legalContent';

// パスワード強度チェック
const checkPasswordStrength = (password) => {
  const checks = {
    length:  password.length >= 8,
    upper:   /[A-Z]/.test(password),
    lower:   /[a-z]/.test(password),
    number:  /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const score = Object.values(checks).filter(Boolean).length;
  return { checks, score };
};

const StrengthBar = ({ score }) => {
  const levels = [
    { label: '非常に弱い', color: '#ef4444' },
    { label: '弱い',       color: '#f97316' },
    { label: '普通',       color: '#eab308' },
    { label: '強い',       color: '#22c55e' },
    { label: '非常に強い', color: '#6366f1' },
  ];
  const level = levels[Math.min(score - 1, 4)] || levels[0];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= score ? level.color : '#334155', transition: 'background 0.2s' }} />
        ))}
      </div>
      {score > 0 && <div style={{ fontSize: 11, color: level.color }}>{level.label}</div>}
    </div>
  );
};

// ── パスワード入力欄（Auth関数の外で定義 → 再レンダリング時に再生成されない）──
const PasswordInput = ({ value, onChange, placeholder, showFlag, setShowFlag, onSubmit, inputStyle, textMuted }) => (
  <div style={{ position: 'relative' }}>
    <input
      type={showFlag ? 'text' : 'password'}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onKeyDown={e => e.key === 'Enter' && onSubmit()}
      style={{ ...inputStyle, paddingRight: 48 }}
    />
    <button
      onClick={() => setShowFlag(!showFlag)}
      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
    >
      {showFlag ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      )}
    </button>
  </div>
);

// ── メール確認待ち画面 ──
const EmailConfirmScreen = ({ email, onBack }) => (
  <div style={{ textAlign: 'center', padding: '8px 0' }}>
    <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: '0 0 12px' }}>
      メールを確認してください
    </h2>
    <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, margin: '0 0 8px' }}>
      <span style={{ color: '#6366f1', fontWeight: 600 }}>{email}</span> に<br />
      確認メールを送信しました。
    </p>
    <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7, margin: '0 0 24px' }}>
      メール内のリンクをクリックすると<br />アカウントが有効化されます。
    </p>
    <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 12, color: '#475569', textAlign: 'left' }}>
      <div style={{ marginBottom: 4 }}><IconPin size={14}/> メールが届かない場合</div>
      <div>・迷惑メールフォルダを確認してください</div>
      <div>・数分待ってから再度確認してください</div>
    </div>
    <button
      onClick={onBack}
      style={{ background: 'none', border: '1px solid #334155', borderRadius: 10, padding: '10px 24px', fontSize: 14, color: '#94a3b8', cursor: 'pointer' }}
    >
      ログイン画面に戻る
    </button>
  </div>
);

export default function Auth({ initialMode = 'login', onPasswordUpdated } = {}) {
  const { theme } = useTheme();
  const [mode, setMode] = useState(initialMode); // login | signup | reset | update_password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [emailConfirmSent, setEmailConfirmSent] = useState(false);

  const { checks, score } = checkPasswordStrength(mode === 'update_password' ? newPassword : password);
  const isPasswordValid = score >= 3 && checks.length;

  // ── パスワードリセットのトークン検知 ──
  // Supabaseのリセットリンクは /#access_token=xxx&type=recovery の形でくる
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setMode('update_password');
      // ハッシュをクリア（URLをきれいにする）
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    // デモから「新規登録」で戻ってきた場合にsignupモードで開く
    if (shouldOpenSignup()) setMode('signup');
  }, []);

  // ── onAuthStateChange で PASSWORD_RECOVERY イベントも拾う（念のため二重対応）──
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setMode('update_password');
        window.history.replaceState(null, '', window.location.pathname);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async () => {
    setMessage({ text: '', type: '' });

    // ── パスワード更新 ──
    if (mode === 'update_password') {
      if (!newPassword) return setMessage({ text: '新しいパスワードを入力してください', type: 'error' });
      if (!isPasswordValid) return setMessage({ text: 'パスワードは8文字以上で英字・数字を含めてください', type: 'error' });
      setLoading(true);
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      setLoading(false);
      if (error) return setMessage({ text: error.message, type: 'error' });
      setMessage({ text: 'パスワードを更新しました！', type: 'success' });
      setTimeout(() => {
        setNewPassword('');
        setMessage({ text: '', type: '' });
        if (onPasswordUpdated) {
          onPasswordUpdated(); // App.js側のisPasswordRecoveryをリセット→アプリへ
        } else {
          setMode('login');
        }
      }, 1500);
      return;
    }

    if (!email.trim()) return setMessage({ text: 'メールアドレスを入力してください', type: 'error' });

    // ── リセットメール送信 ──
    if (mode === 'reset') {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`,
      });
      setLoading(false);
      if (error) return setMessage({ text: error.message, type: 'error' });
      return setMessage({ text: 'パスワードリセットメールを送信しました', type: 'success' });
    }

    if (!password) return setMessage({ text: 'パスワードを入力してください', type: 'error' });

    // ── 新規登録 ──
    if (mode === 'signup') {
      if (!isPasswordValid) return setMessage({ text: 'パスワードは8文字以上で英字・数字を含めてください', type: 'error' });
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({ email, password });
      setLoading(false);
      if (error) return setMessage({ text: error.message, type: 'error' });
      if (data?.user && !data?.user?.identities?.[0]?.identity_data?.email_verified) {
        const { data: authData } = await supabase.auth.getUser();
        if (authData?.user) {
          await supabase.from('profiles').upsert({ id: authData.user.id, name: email.split('@')[0] });
        }
      }
      setEmailConfirmSent(true);
      return;
    }

    // ── ログイン ──
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      const msg = error.message.includes('Invalid') ? 'メールアドレスまたはパスワードが正しくありません' :
                  error.message.includes('Email not confirmed') ? 'メールアドレスの確認が完了していません。届いたメールのリンクをクリックしてください。' :
                  error.message;
      return setMessage({ text: msg, type: 'error' });
    }
  };

  const s = {
    input: {
      width: '100%', background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 12,
      padding: '12px 16px', fontSize: 15, color: theme.textPrimary, outline: 'none', boxSizing: 'border-box',
    },
    label: { fontSize: 13, color: theme.textSecondary, marginBottom: 6, display: 'block' },
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bgInput, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* ロゴ＋キャッチコピー（パスワード更新画面では非表示） */}
        {mode !== 'update_password' && (
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
              <svg width="44" height="44" viewBox="0 0 192 192" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius:14 }}>
                <rect width="192" height="192" rx="42" fill="url(#grada)"/>
                <rect x="38" y="38" width="116" height="34" rx="10" fill="white"/>
                <rect x="38" y="84" width="64" height="34" rx="10" fill="#9B59E8"/>
                <rect x="112" y="84" width="42" height="34" rx="10" fill="#7BA7C8"/>
                <rect x="38" y="130" width="90" height="34" rx="10" fill="#3DBB7C"/>
                <defs>
                  <linearGradient id="grada" x1="0" y1="0" x2="192" y2="192" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#5B6FD4"/>
                    <stop offset="100%" stopColor="#3DBB7C"/>
                  </linearGradient>
                </defs>
              </svg>
              <span style={{ fontSize: 30, fontWeight: 800, color: '#4F46E5', letterSpacing: '-0.5px' }}>Totonoee</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.textPrimary, marginBottom: 6, lineHeight: 1.5 }}>
              フリーランスの<span style={{ color: '#a78bfa' }}>お金と仕事</span>を、<br/>ひとつにまとめる
            </div>
            <div style={{ fontSize: 13, color: theme.textWeak, marginBottom: 20 }}>
              案件・経費・請求・AIアドバイスまで全部入り
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, textAlign: 'left' }}>
              {[
                { icon: <IconProjects size={16}/>, text: '案件・進捗管理' },
                { icon: <IconInvoices size={16}/>, text: '請求書PDF出力' },
                { icon: <IconExpenses size={16}/>, text: '経費・レシート管理' },
                { icon: <IconAi size={16}/>, text: 'AIアドバイス' },
              ].map(item => (
                <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 8, background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 10, padding: '8px 12px' }}>
                  <span style={{ color: '#6366f1' }}>{item.icon}</span>
                  <span style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 500 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* カード */}
        <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 20, padding: 32 }}>

          {/* メール確認待ち画面 */}
          {emailConfirmSent ? (
            <EmailConfirmScreen
              email={email}
              onBack={() => {
                setEmailConfirmSent(false);
                setMode('login');
                setEmail('');
                setPassword('');
                setMessage({ text: '', type: '' });
              }}
            />
          ) : (
            <>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.textPrimary, margin: '0 0 24px' }}>
                {mode === 'login'           ? 'ログイン'
                : mode === 'signup'         ? '新規登録'
                : mode === 'reset'          ? 'パスワードをリセット'
                :                             '新しいパスワードを設定'}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* ── パスワード更新モード ── */}
                {mode === 'update_password' ? (
                  <>
                    <div style={{ background: '#1e3a5f', border: '1px solid #2563a8', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#93c5fd' }}>
                      🔐 新しいパスワードを入力してください
                    </div>
                    <div>
                      <label style={s.label}>新しいパスワード</label>
                      <PasswordInput
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder="8文字以上・英字と数字を含む"
                        showFlag={showNewPassword}
                        setShowFlag={setShowNewPassword}
                        onSubmit={handleSubmit}
                        inputStyle={s.input}
                        textMuted={theme.textMuted}
                      />
                      {newPassword && (
                        <div style={{ marginTop: 8 }}>
                          <StrengthBar score={score} />
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {[
                              { key: 'length',  label: '8文字以上' },
                              { key: 'upper',   label: '大文字' },
                              { key: 'lower',   label: '小文字' },
                              { key: 'number',  label: '数字' },
                              { key: 'special', label: '記号' },
                            ].map(item => (
                              <span key={item.key} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: checks[item.key] ? '#14532d' : '#334155', color: checks[item.key] ? '#86efac' : '#64748b' }}>
                                {checks[item.key] ? '✓' : '·'} {item.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* ── 通常モード（メール入力） ── */}
                    <div>
                      <label style={s.label}>メールアドレス</label>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                        style={s.input}
                      />
                    </div>

                    {/* パスワード（リセット以外） */}
                    {mode !== 'reset' && (
                      <div>
                        <label style={s.label}>パスワード</label>
                        <PasswordInput
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder={mode === 'signup' ? '8文字以上・英字と数字を含む' : 'パスワード'}
                          showFlag={showPassword}
                          setShowFlag={setShowPassword}
                          onSubmit={handleSubmit}
                          inputStyle={s.input}
                          textMuted={theme.textMuted}
                        />
                        {mode === 'signup' && password && (
                          <div style={{ marginTop: 8 }}>
                            <StrengthBar score={score} />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                              {[
                                { key: 'length',  label: '8文字以上' },
                                { key: 'upper',   label: '大文字' },
                                { key: 'lower',   label: '小文字' },
                                { key: 'number',  label: '数字' },
                                { key: 'special', label: '記号' },
                              ].map(item => (
                                <span key={item.key} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: checks[item.key] ? '#14532d' : '#334155', color: checks[item.key] ? '#86efac' : '#64748b' }}>
                                  {checks[item.key] ? '✓' : '·'} {item.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* メッセージ */}
                {message.text && (
                  <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 13, background: message.type === 'error' ? '#450a0a' : '#14532d', color: message.type === 'error' ? '#fca5a5' : '#86efac' }}>
                    {message.text}
                  </div>
                )}

                {/* ボタン */}
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 600, cursor: loading ? 'default' : 'pointer', background: loading ? theme.bgHover : theme.accent, color: loading ? theme.textWeak : theme.accentText, transition: 'all 0.2s' }}
                >
                  {loading ? '処理中...'
                  : mode === 'login'           ? 'ログイン'
                  : mode === 'signup'          ? '無料で始める'
                  : mode === 'reset'           ? 'リセットメールを送る'
                  :                              'パスワードを更新する'}
                </button>

                {/* 規約への同意（新規登録時のみ） */}
                {mode === 'signup' && (
                  <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.9, textAlign: 'center' }}>
                    登録することで、
                    <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
                      style={{ color: theme.accent, textDecoration: 'underline', textUnderlineOffset: 3 }}>利用規約</a>
                    と
                    <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
                      style={{ color: theme.accent, textDecoration: 'underline', textUnderlineOffset: 3 }}>プライバシーポリシー</a>
                    に同意したものとみなします。
                  </div>
                )}

                {/* パスワードを忘れた（ログイン時のみ） */}
                {mode === 'login' && (
                  <button onClick={() => { setMode('reset'); setMessage({ text: '', type: '' }); }}
                    style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: 13, cursor: 'pointer', textAlign: 'center' }}>
                    パスワードをお忘れですか？
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* モード切替（update_passwordとメール確認待ち中は非表示） */}
        {!emailConfirmSent && mode !== 'update_password' && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            {mode === 'login' ? (
              <span style={{ fontSize: 14, color: theme.textMuted }}>
                アカウントをお持ちでない方は{' '}
                <button onClick={() => { setMode('signup'); setMessage({ text: '', type: '' }); setPassword(''); }}
                  style={{ background: 'none', border: 'none', color: theme.accent, fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                  新規登録
                </button>
              </span>
            ) : (
              <span style={{ fontSize: 14, color: theme.textMuted }}>
                すでにアカウントをお持ちの方は{' '}
                <button onClick={() => { setMode('login'); setMessage({ text: '', type: '' }); setPassword(''); }}
                  style={{ background: 'none', border: 'none', color: theme.accent, fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                  ログイン
                </button>
              </span>
            )}
          </div>
        )}

        {/* デモを試すボタン（update_passwordとメール確認待ち中は非表示） */}
        {!emailConfirmSent && mode !== 'update_password' && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              onClick={() => {
                enterDemoMode();
                window.location.href = '/';
              }}
              style={{
                background: 'none',
                border: `1px dashed ${theme.border}`,
                borderRadius: 12,
                padding: '10px 24px',
                fontSize: 13,
                color: theme.textMuted,
                cursor: 'pointer',
                width: '100%',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#f97316'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.textMuted; }}
            >
              登録なしでデモを試す
            </button>
          </div>
        )}

        {/* 法務リンク（未ログイン時のランディングはこの画面。Stripe審査でここから特商法へ辿れる必要がある） */}
        <div style={{
          marginTop: 28, paddingTop: 18, borderTop: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px 14px',
        }}>
          {LEGAL_LINKS.map(l => (
            <a key={l.key} href={l.path}
              style={{ fontSize: 11, color: theme.textWeak, textDecoration: 'underline', textUnderlineOffset: 3 }}>
              {l.label}
            </a>
          ))}
        </div>
        <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: theme.textWeak }}>
          © {new Date().getFullYear()} Digital商店
        </div>

      </div>
    </div>
  );
}
